import { useState, useEffect, useRef } from 'react';

export default function Workflow() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const workflowStages = [
    {
      icon: 'ri-file-text-line',
      title: 'Báo giá',
      description: 'Tạo và quản lý báo giá chuyên nghiệp',
      color: 'from-[#06b6d4] to-[#00c897]'
    },
    {
      icon: 'ri-tools-line',
      title: 'Triển khai',
      description: 'Theo dõi quá trình thực hiện dự án',
      color: 'from-[#2a4b7c] to-[#3d5a80]'
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: 'Nghiệm thu',
      description: 'Kiểm tra chất lượng và xác nhận',
      color: 'from-[#2ecc71] to-[#00c897]'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Thanh toán',
      description: 'Quản lý thanh toán và hóa đơn',
      color: 'from-[#06b6d4] to-[#2ecc71]'
    }
  ];

  const features = [
    {
      icon: 'ri-list-check-2',
      title: 'Checklist theo phase',
      description: 'Đảm bảo không bỏ sót bất kỳ bước nào trong quy trình'
    },
    {
      icon: 'ri-user-follow-line',
      title: 'Phê duyệt nhiều cấp',
      description: 'Thiết lập quy trình phê duyệt linh hoạt theo cấp bậc'
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'SLA & Cảnh báo',
      description: 'Cảnh báo tự động khi trễ tiến độ hoặc vi phạm SLA'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Kiểm soát chất lượng',
      description: 'Đảm bảo chất lượng đầu ra ở mọi giai đoạn'
    }
  ];

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className={`text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#2a4b7c]/10 text-[#2a4b7c] text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            <i className="ri-flow-chart"></i>
            <span>WORKFLOW ENGINE</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Quản lý quy trình công việc chuẩn hóa
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Hệ thống cho phép xây dựng và vận hành <strong className="text-[#2a4b7c]">quy trình làm việc chuẩn hóa</strong>, 
            giúp doanh nghiệp kiểm soát chất lượng, giảm rủi ro và tăng hiệu suất vận hành
          </p>
        </div>

        {/* Workflow Stages */}
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12 md:mb-16">
          {workflowStages.map((stage, index) => (
            <div key={index} className={`relative transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`} style={{ transitionDelay: `${200 + index * 100}ms` }}>
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100 hover:border-[#06b6d4]/30">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${stage.color} mb-3 sm:mb-4`}>
                  <i className={`${stage.icon} text-xl sm:text-2xl text-white`}></i>
                </div>
                <div className="text-xs sm:text-sm font-bold text-[#06b6d4] mb-1 sm:mb-2">GIAI ĐOẠN {index + 1}</div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">{stage.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600">{stage.description}</p>
              </div>
              {index < workflowStages.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                  <i className="ri-arrow-right-line text-2xl text-[#06b6d4]"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div key={index} className={`flex items-start space-x-3 sm:space-x-4 p-4 sm:p-6 rounded-xl bg-white shadow-md hover:shadow-lg transition-all border border-gray-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`} style={{ transitionDelay: `${600 + index * 100}ms` }}>
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-[#2ecc71] to-[#00c897] flex-shrink-0">
                <i className={`${feature.icon} text-lg sm:text-xl text-white`}></i>
              </div>
              <div>
                <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">{feature.title}</h4>
                <p className="text-xs sm:text-sm text-gray-600">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits Banner */}
        <div className={`mt-8 sm:mt-12 md:mt-16 bg-gradient-to-r from-[#2a4b7c] to-[#3d5a80] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-white text-center transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6">
            Lợi ích vượt trội từ Workflow Engine
          </h3>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-[#00c897] mb-2">85%</div>
              <p className="text-sm sm:text-base text-white/80">Giảm thời gian xử lý</p>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-[#00c897] mb-2">92%</div>
              <p className="text-sm sm:text-base text-white/80">Tăng độ chính xác</p>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-[#00c897] mb-2">78%</div>
              <p className="text-sm sm:text-base text-white/80">Giảm rủi ro vận hành</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
